/**
 * AvA Behavior Studio — Command Centre card (Sprint 190)
 * ======================================================
 *
 * Master-admin-only editor for the AI Practice Partner system prompts.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │  [Lock badge] Behavior Studio · Robert only                 │
 *   │                                                              │
 *   │  Role tabs: [Love / Health] [Friend / Happiness] [Success]  │
 *   │                                                              │
 *   │  ┌────────────────────────────────────────────────────────┐ │
 *   │  │ Base prompt (active)            [v] Version history    │ │
 *   │  │ ┌────────────────────────────────────────────────────┐ │ │
 *   │  │ │ <textarea, monospace, 12 rows>                      │ │ │
 *   │  │ └────────────────────────────────────────────────────┘ │ │
 *   │  │                          [Reset]  [Test in sandbox]     │ │
 *   │  │                                            [Save active] │ │
 *   │  └────────────────────────────────────────────────────────┘ │
 *   │                                                              │
 *   │  Per-character voice override (optional)                    │
 *   │  Character: [______] Override: [textarea]  [+ Save]         │
 *   │                                                              │
 *   │  Sandbox · drop-down sample teen messages, see live reply   │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Read endpoint is open to any admin; mutations gate to master admin via
 * the backend's 403 → we surface a clear "Read-only" badge.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Brain, Loader2, Save, History, Beaker, Lock,
  RotateCcw, Plus, Trash2, AlertCircle, CheckCircle, Eye,
} from 'lucide-react';
import { apiClient } from '../../api/client';

const BRAND_PURPLE = '#7B4FD8';
const BRAND_ORANGE = '#F5831F';
const BRAND_INK = '#1A1209';

const ROLE_TABS = [
  { id: 'health', label: 'Love / Health', color: '#DC2626', desc: 'Emotional well-being + relationships' },
  { id: 'happiness', label: 'Friend / Happiness', color: BRAND_PURPLE, desc: 'Friendship + social skills' },
  { id: 'wealth', label: 'Success / Wealth', color: BRAND_ORANGE, desc: 'Career + assertiveness' },
];

const SAMPLE_MESSAGES = [
  'How do I start a conversation with someone new at school?',
  "I'm scared to ask my crush to hang out. What do I say?",
  "I have a job interview tomorrow. How do I handle nerves?",
  "My friend ignored me all week. Should I confront them?",
];

const AvABehaviorStudio = () => {
  const [data, setData] = useState(null);
  const [activeRole, setActiveRole] = useState('happiness');
  const [draftPrompt, setDraftPrompt] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [sandboxMsg, setSandboxMsg] = useState(SAMPLE_MESSAGES[0]);
  const [sandboxReply, setSandboxReply] = useState(null);
  const [charOverride, setCharOverride] = useState({ character_name: '', override: '' });

  const load = useCallback(async () => {
    try {
      const d = await apiClient.request('/api/admin/ava/prompts');
      setData(d);
      const r = d.roles.find((x) => x.role === activeRole);
      if (r) { setDraftPrompt(r.active_prompt); setDirty(false); }
    } catch (e) {
      if (!(e.message || '').includes('403')) console.warn('ava-behavior load', e);
    }
  }, [activeRole]);

  useEffect(() => { load(); }, [load]);

  const currentRoleData = data?.roles?.find((r) => r.role === activeRole);
  const isEditable = !!data?.editable;

  const switchRole = (roleId) => {
    if (dirty && !window.confirm('Discard unsaved changes to this role prompt?')) return;
    setActiveRole(roleId);
    setSandboxReply(null);
    setShowHistory(false);
    setErr(null); setMsg(null);
  };

  const handlePromptChange = (v) => {
    setDraftPrompt(v);
    setDirty(currentRoleData ? v !== currentRoleData.active_prompt : true);
  };

  const savePrompt = async () => {
    if (!draftPrompt.trim() || draftPrompt.length < 20) {
      setErr('Prompt must be at least 20 characters.'); return;
    }
    const note = window.prompt('Optional change note for the audit log:', '') || '';
    if (!window.confirm(
      `Save this prompt as the active "${currentRoleData?.label || activeRole}" base?\n\n` +
      `It will apply to EVERY teen-facing message from the next chat onwards.`
    )) return;
    setBusy('save'); setErr(null); setMsg(null);
    try {
      const res = await apiClient.request(`/api/admin/ava/prompts/${activeRole}`, {
        method: 'PUT',
        body: JSON.stringify({ prompt: draftPrompt, notes: note.slice(0, 200) }),
      });
      if (res.success) {
        setMsg(`Saved · v${res.version_id} by ${res.edited_by}`);
        setDirty(false);
        load();
      } else {
        setErr(res.detail || 'Save failed');
      }
    } catch (e) { setErr(e.message || 'Save failed'); }
    setBusy('');
  };

  const resetToFallback = () => {
    if (!currentRoleData?.fallback_prompt) return;
    if (!window.confirm('Reset draft to the hardcoded fallback prompt? (Not saved until you click Save active.)')) return;
    setDraftPrompt(currentRoleData.fallback_prompt);
    setDirty(currentRoleData.fallback_prompt !== currentRoleData.active_prompt);
  };

  const runSandbox = async () => {
    setBusy('sandbox'); setErr(null); setSandboxReply(null);
    try {
      const res = await apiClient.request('/api/admin/ava/sandbox', {
        method: 'POST',
        body: JSON.stringify({
          role: activeRole,
          draft_prompt: draftPrompt,
          character_override: charOverride.override || '',
          character_name: charOverride.character_name || 'Maya',
          user_message: sandboxMsg,
        }),
      });
      if (res.success) setSandboxReply(res.ai_response);
      else setErr(res.detail || 'Sandbox failed');
    } catch (e) { setErr(e.message || 'Sandbox failed'); }
    setBusy('');
  };

  const loadHistory = async () => {
    setBusy('history');
    try {
      const res = await apiClient.request(`/api/admin/ava/prompts/${activeRole}/history`);
      setHistory(res.versions || []);
      setShowHistory(true);
    } catch (e) { setErr(e.message || 'History load failed'); }
    setBusy('');
  };

  const rollback = async (version_id) => {
    if (!window.confirm(`Rollback to version ${version_id}?\n\nThis becomes the new active prompt immediately.`)) return;
    setBusy(`rollback:${version_id}`); setErr(null); setMsg(null);
    try {
      const res = await apiClient.request(`/api/admin/ava/prompts/${activeRole}/rollback/${version_id}`, { method: 'POST' });
      if (res.success) { setMsg(`Rolled back · new version ${res.new_version_id}`); load(); }
      else setErr(res.detail || 'Rollback failed');
    } catch (e) { setErr(e.message || 'Rollback failed'); }
    setBusy('');
  };

  const saveCharOverride = async () => {
    const name = charOverride.character_name.trim();
    const ov = charOverride.override.trim();
    if (!name || ov.length < 10) {
      setErr('Character name + at least 10 chars of override required.'); return;
    }
    setBusy('char-save'); setErr(null); setMsg(null);
    try {
      const res = await apiClient.request('/api/admin/ava/character-override', {
        method: 'PUT',
        body: JSON.stringify({ character_name: name, override: ov }),
      });
      if (res.success) {
        setMsg(`Override saved for ${name}`);
        setCharOverride({ character_name: '', override: '' });
        load();
      } else { setErr(res.detail || 'Override save failed'); }
    } catch (e) { setErr(e.message || 'Override save failed'); }
    setBusy('');
  };

  const clearCharOverride = async (name) => {
    if (!window.confirm(`Remove override for "${name}"? The character will revert to the role base prompt.`)) return;
    setBusy(`char-del:${name}`);
    try {
      await apiClient.request(`/api/admin/ava/character-override/${encodeURIComponent(name)}`, { method: 'DELETE' });
      setMsg(`Override cleared for ${name}`);
      load();
    } catch (e) { setErr(e.message || 'Clear failed'); }
    setBusy('');
  };

  if (!data) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="ava-behavior-studio">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EDE9FE' }}>
          <Brain size={18} style={{ color: BRAND_PURPLE }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: BRAND_INK }}>
            AvA Behavior Studio
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: isEditable ? '#D1FAE5' : '#F3F4F6', color: isEditable ? '#065F46' : '#6B7280' }}>
              <Lock size={9} /> {isEditable ? 'Editor mode' : 'Read-only · master admin required'}
            </span>
          </h3>
          <p className="text-xs" style={{ color: '#666' }}>
            Live edits to the system prompts that shape every Practice Partner response. Changes apply on next message — no redeploy.
          </p>
        </div>
      </div>

      {/* Flash */}
      {(msg || err) && (
        <div className="text-xs mb-3 px-3 py-2 rounded-md flex items-start gap-2"
             style={{ backgroundColor: err ? '#FEE2E2' : '#D1FAE5', color: err ? '#991B1B' : '#065F46' }}>
          {err ? <AlertCircle size={13} className="mt-0.5 flex-shrink-0" /> : <CheckCircle size={13} className="mt-0.5 flex-shrink-0" />}
          <span>{err || msg}</span>
        </div>
      )}

      {/* Role tabs */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="ava-role-tabs">
        {ROLE_TABS.map((t) => {
          const r = data.roles.find((x) => x.role === t.id);
          const isActive = activeRole === t.id;
          return (
            <button
              key={t.id}
              onClick={() => switchRole(t.id)}
              className="flex-1 min-w-[180px] text-left px-3 py-2 rounded-lg border transition-colors"
              style={{
                borderColor: isActive ? t.color : '#E5E7EB',
                backgroundColor: isActive ? `${t.color}11` : '#FAFAFA',
              }}
              data-testid={`ava-role-tab-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold" style={{ color: t.color }}>{t.label}</span>
                {r?.is_overridden && (
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: t.color, color: 'white' }}>EDITED</span>
                )}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: '#666' }}>{t.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Prompt editor */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#666' }}>
            Base prompt {dirty && <span style={{ color: BRAND_ORANGE }}>· unsaved</span>}
          </label>
          <div className="flex items-center gap-2 text-[10px]" style={{ color: '#6B6257' }}>
            <span>{draftPrompt.length} chars</span>
            {currentRoleData?.edited_at && (
              <span>· last edit {new Date(currentRoleData.edited_at).toLocaleDateString()} by {currentRoleData.edited_by}</span>
            )}
          </div>
        </div>
        <textarea
          value={draftPrompt}
          onChange={(e) => handlePromptChange(e.target.value)}
          rows={12}
          disabled={!isEditable}
          className="w-full px-3 py-2 text-xs rounded-md resize-y font-mono"
          style={{ border: '1px solid #E0E0E0', backgroundColor: isEditable ? '#FAFAFA' : '#F3F4F6', color: BRAND_INK, lineHeight: 1.5 }}
          data-testid="ava-prompt-textarea"
        />
        <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
          <div className="flex items-center gap-2">
            <button
              onClick={loadHistory}
              disabled={busy === 'history'}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md"
              style={{ color: '#6B7280', backgroundColor: 'white', border: '1px solid #E5E7EB' }}
              data-testid="ava-history-btn"
            >
              {busy === 'history' ? <Loader2 size={11} className="animate-spin" /> : <History size={11} />}
              Version history
            </button>
            {isEditable && (
              <button
                onClick={resetToFallback}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md"
                style={{ color: '#6B7280', backgroundColor: 'white', border: '1px solid #E5E7EB' }}
                data-testid="ava-reset-btn"
              >
                <RotateCcw size={11} /> Reset to fallback
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditable && (
              <button
                onClick={savePrompt}
                disabled={!dirty || busy === 'save'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white disabled:opacity-50"
                style={{ backgroundColor: BRAND_PURPLE }}
                data-testid="ava-save-btn"
              >
                {busy === 'save' ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save active
              </button>
            )}
          </div>
        </div>
      </div>

      {/* History list */}
      {showHistory && (
        <div className="mb-3 p-3 rounded-md" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }} data-testid="ava-history-list">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#666' }}>Version history · last 20</span>
            <button onClick={() => setShowHistory(false)} className="text-[10px]" style={{ color: '#666' }}>Hide</button>
          </div>
          {history.length === 0 ? (
            <p className="text-[11px] italic" style={{ color: '#9CA3AF' }}>No saved versions yet — the active prompt is the hardcoded fallback.</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {history.map((v) => (
                <div key={v.version_id} className="flex items-center gap-2 text-[11px] p-2 rounded" style={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}>
                  <span className="font-mono" style={{ color: '#6B7280' }}>{v.version_id}</span>
                  <span style={{ color: '#374151' }}>{new Date(v.edited_at).toLocaleString()}</span>
                  <span style={{ color: '#6B7280' }}>· {v.edited_by}</span>
                  {v.notes && <span className="italic truncate" style={{ color: '#9CA3AF' }} title={v.notes}>· {v.notes}</span>}
                  {isEditable && (
                    <button
                      onClick={() => rollback(v.version_id)}
                      disabled={busy === `rollback:${v.version_id}`}
                      className="ml-auto text-[10px] px-2 py-0.5 rounded font-semibold"
                      style={{ color: BRAND_PURPLE, backgroundColor: '#EDE9FE' }}
                      data-testid={`ava-rollback-${v.version_id}`}
                    >
                      {busy === `rollback:${v.version_id}` ? '…' : 'Rollback'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sandbox */}
      <div className="p-3 rounded-md mb-3" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }} data-testid="ava-sandbox">
        <div className="flex items-center gap-2 mb-2">
          <Beaker size={12} style={{ color: '#92400E' }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#92400E' }}>Test in Sandbox</span>
          <span className="text-[10px]" style={{ color: '#92400E' }}>Fires ONE LLM call against the DRAFT — does not save or affect live chats.</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mb-2">
          <select
            value={sandboxMsg}
            onChange={(e) => setSandboxMsg(e.target.value)}
            className="flex-1 px-2 py-1.5 text-xs rounded-md"
            style={{ border: '1px solid #E0E0E0', backgroundColor: 'white' }}
            data-testid="ava-sandbox-message-select"
          >
            {SAMPLE_MESSAGES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button
            onClick={runSandbox}
            disabled={busy === 'sandbox' || draftPrompt.length < 20}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-md text-white disabled:opacity-50"
            style={{ backgroundColor: '#92400E' }}
            data-testid="ava-sandbox-run-btn"
          >
            {busy === 'sandbox' ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
            See what a teen would get
          </button>
        </div>
        {sandboxReply && (
          <div className="p-3 rounded-md" style={{ backgroundColor: 'white', border: '1px solid #FCD34D' }} data-testid="ava-sandbox-reply">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#92400E' }}>AvA replies:</p>
            <p className="text-sm" style={{ color: BRAND_INK, fontFamily: 'Georgia, serif', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{sandboxReply}</p>
          </div>
        )}
      </div>

      {/* Character overrides */}
      <div className="p-3 rounded-md" style={{ backgroundColor: '#FAFAFA', border: '1px dashed #E0E0E0' }} data-testid="ava-character-overrides">
        <div className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: BRAND_PURPLE }}>
          Per-character voice overrides ({data.character_overrides?.length || 0})
        </div>
        {data.character_overrides?.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {data.character_overrides.map((o) => (
              <div key={o.character_name} className="flex items-start gap-2 p-2 rounded text-xs" style={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}>
                <span className="font-bold flex-shrink-0" style={{ color: BRAND_PURPLE }}>{o.character_name}</span>
                <span className="flex-1 italic" style={{ color: '#374151' }}>&ldquo;{o.override}&rdquo;</span>
                {isEditable && (
                  <button onClick={() => clearCharOverride(o.character_name)} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ color: '#991B1B', backgroundColor: '#FEE2E2' }} data-testid={`ava-char-remove-${o.character_name}`}>
                    <Trash2 size={10} /> Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {isEditable && (
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            <input
              type="text"
              placeholder="Character name (e.g. Maya)"
              value={charOverride.character_name}
              onChange={(e) => setCharOverride({ ...charOverride, character_name: e.target.value })}
              className="sm:col-span-1 px-2 py-1.5 text-xs rounded-md"
              style={{ border: '1px solid #E0E0E0', backgroundColor: 'white' }}
              data-testid="ava-char-name-input"
            />
            <input
              type="text"
              placeholder='Voice override (e.g. "speak with dry British humour")'
              value={charOverride.override}
              onChange={(e) => setCharOverride({ ...charOverride, override: e.target.value.slice(0, 2000) })}
              className="sm:col-span-3 px-2 py-1.5 text-xs rounded-md"
              style={{ border: '1px solid #E0E0E0', backgroundColor: 'white' }}
              data-testid="ava-char-override-input"
            />
            <button
              onClick={saveCharOverride}
              disabled={busy === 'char-save'}
              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: BRAND_PURPLE }}
              data-testid="ava-char-save-btn"
            >
              {busy === 'char-save' ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvABehaviorStudio;
